import { useRef, useState } from "react";
import { startOfDay } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { List, Calendar as CalendarIcon } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useOrderDeletion } from "@/hooks/useOrderDeletion";
import { useOrdersQuery, DEFAULT_ORDERS_PAGE_SIZE } from "@/hooks/useOrdersQuery";
import { useEapiStatusSync } from "@/hooks/useEapiStatusSync";
import { useOrderOosRealtime } from "@/hooks/useOrderOosRealtime";
import { useOrderMutations } from "@/hooks/useOrderMutations";
import { useOrdersExport } from "@/hooks/useOrdersExport";
import { useUrlFilterState, useUrlArrayFilterState } from "@/hooks/useUrlFilterState";
import { OrderFilters } from "@/components/orders/OrderFilters";
import { OrderFormDialog } from "@/components/orders/OrderFormDialog";
import { OrderEditDialog } from "@/components/orders/OrderEditDialog";
import { ImportOrdersDialog } from "@/components/orders/ImportOrdersDialog";
import { OrderListView } from "@/components/orders/OrderListView";
import { OrderBoardView } from "@/components/orders/OrderBoardView";
import { OrderFormData, initialFormData } from "@/components/orders/orderFormTypes";
import {
  Order,
  Customer,
  Tractor,
  Driver,
  Location,
} from "@/types/tms";
import { Button } from "@/components/ui/button";
import { ExpandingSearch } from "@/components/ui/expanding-search";
import { Plus, Download, Loader2, ChevronDown, Upload } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/hooks/use-toast";
import { useRememberFilters } from "@/hooks/useRememberFilters";
import { useTenant } from "@/hooks/useTenant";
import { PROD_TENANT_SLUG } from "@/lib/constants";
import { useStarredOrderIds } from "@/hooks/useOrderStars";
import { ChangeReasonDialog } from "@/components/orders/ChangeReasonDialog";
import { RecurringOrdersSubTable } from "@/components/customers/RecurringOrdersSubTable";
import { useChangeEventPrompt, recordChangeEvents } from "@/hooks/useChangeEventPrompt";
import type { ChangeEventReason } from "@/lib/change-events";

export default function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pageTab, setPageTab] = useState<"orders" | "templates">("orders");
  const { activeTenant, tenantId } = useTenant();
  // Stash the captured manual-create reason between the create prompt and
  // `onCreated` so we can attribute the change_events row to the new order id.
  const pendingManualReasonRef = useRef<{
    reason: ChangeEventReason;
    otherText: string | null;
  } | null>(null);
  const { promptForReasons: promptManualCreate, dialogProps: manualCreateDialogProps } =
    useChangeEventPrompt();

  useRememberFilters();

  const isProd = activeTenant === PROD_TENANT_SLUG;
  const [importOpen, setImportOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useUrlFilterState<string>("q", "");
  const [statusFilter, setStatusFilter] = useUrlArrayFilterState("status");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [formData, setFormData] = useState<OrderFormData>(initialFormData);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useUrlFilterState<string>("tab", "list");
  const today = startOfDay(new Date());
  const [dateFilter, setDateFilter] = useUrlFilterState<{ from: Date; to: Date } | null>(
    "date",
    { from: today, to: today },
    (v) => {
      if (!v) return "none";
      const fmt = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };
      return `${fmt(v.from)}_${fmt(v.to)}`;
    },
    (s) => {
      if (s === "none") return null;
      const [from, to] = s.split("_");
      const parse = (str: string) => {
        const [y, m, d] = str.split("-").map(Number);
        return startOfDay(new Date(y, m - 1, d));
      };
      try {
        return { from: parse(from), to: parse(to) };
      } catch {
        return { from: today, to: today };
      }
    },
  );
  const [editOrder, setEditOrder] = useState<Order | null>(null);
  const [shipFromFilter, setShipFromFilter] = useUrlArrayFilterState("shipFrom");
  const [shipToFilter, setShipToFilter] = useUrlArrayFilterState("shipTo");
  const [customerFilter, setCustomerFilter] = useUrlArrayFilterState("customer");
  const [tenderFilter, setTenderFilter] = useUrlFilterState<string>("tender", "all");
  const [bolFilter, setBolFilter] = useUrlFilterState<string>("bol", "all");
  const [tenderIdFilter, setTenderIdFilter] = useUrlFilterState<string>("tenderId", "all");
  const [trailerTypeFilter, setTrailerTypeFilter] = useUrlArrayFilterState("trailerType");
  const [trailerLoadStatusFilter, setTrailerLoadStatusFilter] = useUrlArrayFilterState("trailerLoadStatus");
  const [apiCreatedFilter, setApiCreatedFilter] = useUrlFilterState<string>("apiCreated", "all");
  const [tripTypeFilter, setTripTypeFilter] = useUrlArrayFilterState("tripType");
  const [orderTypeFilter, setOrderTypeFilter] = useUrlFilterState<string>("orderType", "all");
  const [subscribedOnly, setSubscribedOnly] = useUrlFilterState<boolean>(
    "subscribed",
    false,
    (v) => (v ? "1" : "0"),
    (s) => s === "1",
  );
  const [pageSize, setPageSize] = useUrlFilterState<number>(
    "pageSize",
    DEFAULT_ORDERS_PAGE_SIZE,
    (v) => String(v),
    (s) => {
      const n = parseInt(s, 10);
      return Number.isFinite(n) && n > 0 ? n : DEFAULT_ORDERS_PAGE_SIZE;
    },
  );

  const handleSearchChange = (value: string) => { setSearchQuery(value); setCurrentPage(1); };
  const handleStatusFilterChange = (value: string[]) => { setStatusFilter(value); setCurrentPage(1); };
  const handleDateFilterChange = (date: { from: Date; to: Date } | null) => { setDateFilter(date); setCurrentPage(1); };

  const { data: customers } = useQuery({
    queryKey: ["customers", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      if (!tenantId) return [] as Customer[];
      const { data } = await supabase.from("customers").select("*").order("organization_name");
      return data as Customer[];
    },
  });

  const locationsQuery = useQuery({
    queryKey: ["locations", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      if (!tenantId) return [] as Location[];
      const { data, error } = await supabase.from("locations").select("*").eq("archived", false).order("short_code");
      if (error) throw error;
      return data as Location[];
    },
  });
  const locations = locationsQuery.data;

  const { data: tractors } = useQuery({
    queryKey: ["tractors", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      if (!tenantId) return [] as Tractor[];
      const { data } = await supabase.from("tractors").select("*").order("name");
      return data as Tractor[];
    },
  });

  const { data: drivers } = useQuery({
    queryKey: ["drivers", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      if (!tenantId) return [] as Driver[];
      const { data } = await supabase.from("drivers").select("*").order("name");
      return data as Driver[];
    },
  });

  const starredOrdersQuery = useStarredOrderIds();
  const starredOrderIds = starredOrdersQuery.data;
  const subscribedIds = subscribedOnly ? Array.from(starredOrderIds ?? []) : undefined;
  const hasLocationFilter = shipFromFilter.some((v) => v !== "all") || shipToFilter.some((v) => v !== "all");
  const dependenciesReady = (!hasLocationFilter || locationsQuery.isSuccess) && (!subscribedOnly || starredOrdersQuery.isSuccess);
  const dependencyError = hasLocationFilter && locationsQuery.error
    ? locationsQuery.error as Error
    : subscribedOnly && starredOrdersQuery.error
      ? starredOrdersQuery.error as Error
      : null;

  const { orders, totalCount, totalPages, isLoading, isFetching, isError, error: ordersError, refetch: refetchOrders } = useOrdersQuery({
    searchQuery, statusFilter, currentPage, dateFilter, tenderFilter, bolFilter, tripTypeFilter, orderTypeFilter,
    tenderIdFilter, trailerTypeFilter, trailerLoadStatusFilter, apiCreatedFilter,
    shipFromLocationFilter: shipFromFilter, shipToLocationFilter: shipToFilter, customerFilter, pageSize, locations,
    subscribedOrderIds: subscribedIds,
    dependenciesReady,
    dependencyError,
  });

  const { exportToCsv, isExporting } = useOrdersExport({
    searchQuery, statusFilter, dateFilter, tenderFilter, bolFilter, tripTypeFilter, orderTypeFilter,
    tenderIdFilter, trailerTypeFilter, trailerLoadStatusFilter, apiCreatedFilter,
    shipFromLocationFilter: shipFromFilter, shipToLocationFilter: shipToFilter, customerFilter, locations,
  });

  const handleExport = async () => {
    try {
      const res = await exportToCsv();
      toast({
        title: "Export complete",
        description: `Exported ${res?.count ?? 0} order${res?.count === 1 ? "" : "s"}.`,
      });
    } catch (err) {
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : "Unable to export orders.",
        variant: "destructive",
      });
    }
  };

  const hasActiveFilter =
    !!searchQuery.trim() ||
    statusFilter.some((v) => v && v !== "all") ||
    tenderFilter !== "all" ||
    bolFilter !== "all" ||
    tenderIdFilter !== "all" ||
    trailerTypeFilter.some((v) => v && v !== "all") ||
    trailerLoadStatusFilter.some((v) => v && v !== "all") ||
    apiCreatedFilter !== "all" ||
    tripTypeFilter.some((v) => v && v !== "all") ||
    orderTypeFilter !== "all" ||
    (shipFromFilter ?? []).some((v) => v && v !== "all") ||
    (shipToFilter ?? []).some((v) => v && v !== "all") ||
    (customerFilter ?? []).some((v) => v && v !== "all");

  // On-demand EAPI status sync for visible orders
  useEapiStatusSync(orders);

  // Live reaction to tractor OOS unassignments coming from /truck-status.
  useOrderOosRealtime();

  const resetForm = () => {
    setFormData(initialFormData);
    setIsCreateOpen(false);
  };

  const { createMutation, updateMutation, bulkCreateMutation } = useOrderMutations({
    userEmail: user?.email,
    tractors,
    drivers,
    locations,
    onSuccess: resetForm,
    onCreated: (orderId) => {
      const pending = pendingManualReasonRef.current;
      if (pending && tenantId) {
        void recordChangeEvents([
          {
            orderId,
            tenantId,
            eventType: "trip_created_manual",
            reason: pending.reason,
            otherText: pending.otherText,
            newValue: { trip_type: "Manual" },
          },
        ]).catch((err) => console.warn("[Orders] manual-create change_events failed", err));
      }
      pendingManualReasonRef.current = null;
      navigate(`/orders/${orderId}`);
    },
    onTemplateCreated: (templateId) => {
      pendingManualReasonRef.current = null;
      navigate(`/templates/${templateId}`);
    },
  });

  const { deleteOrder, isDeleting } = useOrderDeletion();

  const handleEdit = (order: Order) => {
    setEditOrder(order);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Manual-trip create requires a canonical reason code (matrix).
    if (formData.trip_type === "Manual") {
      const results = await promptManualCreate(
        [{ key: "manual", eventType: "trip_created_manual" }],
      );
      if (!results) return; // dismissed
      pendingManualReasonRef.current = {
        reason: results[0].reason,
        otherText: results[0].otherText,
      };
    } else {
      pendingManualReasonRef.current = null;
    }
    createMutation.mutate(formData);
  };

  return (
    <AppLayout title="Orders" subtitle={
      <nav className="flex gap-6 -mb-px">
        {([
          { key: "orders" as const, label: "All orders", icon: <List className="h-4 w-4" /> },
          { key: "templates" as const, label: "Recurring order templates", icon: <CalendarIcon className="h-4 w-4" /> },
        ]).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setPageTab(tab.key)}
            className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
              pageTab === tab.key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </nav>
    } actions={
      <div className={`items-center gap-2 ${pageTab === "orders" ? "flex" : "hidden"}`}>
        {/* Desktop: separate Import + Export buttons */}
        <div className="hidden md:flex items-center gap-2">
          <ImportOrdersDialog
            onImport={(rows) => bulkCreateMutation.mutate(rows)}
            isImporting={bulkCreateMutation.isPending}
          />
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={isExporting || isLoading}
            aria-label="Export Orders"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Export Orders
          </Button>
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Order
          </Button>
        </div>

        {/* Tablet/Mobile: split button — New Order + dropdown for Import/Export */}
        <div className="flex md:hidden items-center">
          <Button
            onClick={() => setIsCreateOpen(true)}
            className="rounded-r-none border-r border-primary-foreground/20"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Order
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label="More order actions"
                className="rounded-l-none px-2"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setImportOpen(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Import Orders
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={handleExport}
                disabled={isExporting || isLoading}
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export Orders
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Headless dialog controlled by the split-button menu */}
          <ImportOrdersDialog
            onImport={(rows) => bulkCreateMutation.mutate(rows)}
            isImporting={bulkCreateMutation.isPending}
            open={importOpen}
            onOpenChange={setImportOpen}
            hideTrigger
          />
        </div>
      </div>
    }>
      <OrderFormDialog
        isOpen={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        editingOrder={null}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        onReset={resetForm}
        isSubmitting={createMutation.isPending}
        customers={customers}
        tractors={tractors}
        drivers={drivers}
        locations={locations}
      />

      <OrderEditDialog
        order={editOrder}
        open={!!editOrder}
        onOpenChange={(open) => { if (!open) setEditOrder(null); }}
      />

      {/* Manual-trip create reason capture (matrix-canonical). */}
      <ChangeReasonDialog
        {...manualCreateDialogProps}
        isPending={createMutation.isPending}
      />

      {pageTab === "templates" ? (
        <div className="animate-fade-in">
          <RecurringOrdersSubTable />
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full w-full min-w-0 overflow-hidden animate-fade-in">
          <div className="flex items-center gap-4 flex-wrap shrink-0 pb-4">
            <OrderFilters
              statusFilter={statusFilter}
              onStatusFilterChange={handleStatusFilterChange}
              dateFilter={dateFilter}
              onDateFilterChange={handleDateFilterChange}
              shipFromFilter={shipFromFilter}
              onShipFromFilterChange={(v) => { setShipFromFilter(v); setCurrentPage(1); }}
              shipToFilter={shipToFilter}
              onShipToFilterChange={(v) => { setShipToFilter(v); setCurrentPage(1); }}
              locations={locations ?? []}
              customerFilter={customerFilter}
              onCustomerFilterChange={(v) => { setCustomerFilter(v); setCurrentPage(1); }}
              customers={customers ?? []}
              tenderFilter={tenderFilter}
              onTenderFilterChange={(v) => { setTenderFilter(v); setCurrentPage(1); }}
              bolFilter={bolFilter}
              onBolFilterChange={(v) => { setBolFilter(v); setCurrentPage(1); }}
              tenderIdFilter={tenderIdFilter}
              onTenderIdFilterChange={(v) => { setTenderIdFilter(v); setCurrentPage(1); }}
              trailerTypeFilter={trailerTypeFilter}
              onTrailerTypeFilterChange={(v: string[]) => { setTrailerTypeFilter(v); setCurrentPage(1); }}
              trailerLoadStatusFilter={trailerLoadStatusFilter}
              onTrailerLoadStatusFilterChange={(v: string[]) => { setTrailerLoadStatusFilter(v); setCurrentPage(1); }}
              apiCreatedFilter={apiCreatedFilter}
              onApiCreatedFilterChange={(v) => { setApiCreatedFilter(v); setCurrentPage(1); }}
              tripTypeFilter={tripTypeFilter}
              onTripTypeFilterChange={(v: string[]) => { setTripTypeFilter(v); setCurrentPage(1); }}
              orderTypeFilter={orderTypeFilter}
              onOrderTypeFilterChange={(v) => { setOrderTypeFilter(v); setCurrentPage(1); }}
              isProd={isProd}
              searchQuery={searchQuery}
              onClearSearch={() => handleSearchChange("")}
              subscribedOnly={subscribedOnly}
              onSubscribedOnlyChange={(v) => { setSubscribedOnly(v); setCurrentPage(1); }}
            />
            <div className="flex items-center gap-3 ml-auto">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Showing {totalCount} order{totalCount === 1 ? "" : "s"}</span>
              <ExpandingSearch value={searchQuery} onChange={handleSearchChange} placeholder="Search orders..." />
              <TabsList>
                <TabsTrigger value="list" className="flex items-center gap-2">
                  <List className="h-4 w-4" /> List
                </TabsTrigger>
                <TabsTrigger value="board" className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" /> Board
                </TabsTrigger>
              </TabsList>
            </div>
          </div>

          <TabsContent value="list" className="mt-0 flex-1 min-h-0 flex flex-col data-[state=inactive]:hidden">
            <OrderListView
              orders={orders}
              isLoading={isLoading}
              isFetching={isFetching}
              isError={isError}
              error={ordersError}
              onRetry={() => { void refetchOrders(); }}
              totalCount={totalCount}
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              pageSize={pageSize}
              onPageSizeChange={(n) => { setPageSize(n); setCurrentPage(1); }}
              onEdit={handleEdit}
              onDelete={deleteOrder}
              isDeleting={isDeleting}
              locations={locations}
              hasActiveFilter={hasActiveFilter}
            />
          </TabsContent>

          <TabsContent value="board" className="mt-0 flex-1 min-h-0 w-full min-w-0 max-w-full overflow-auto data-[state=inactive]:hidden">
            <OrderBoardView
              searchQuery={searchQuery}
              statusFilter={statusFilter}
              shipFromFilter={shipFromFilter}
              shipToFilter={shipToFilter}
              dateFilter={dateFilter}
              tenderFilter={tenderFilter}
              bolFilter={bolFilter}
              tenderIdFilter={tenderIdFilter}
              trailerTypeFilter={trailerTypeFilter}
              trailerLoadStatusFilter={trailerLoadStatusFilter}
              apiCreatedFilter={apiCreatedFilter}
              tripTypeFilter={tripTypeFilter}
              orderTypeFilter={orderTypeFilter}
              locations={locations}
              subscribedOnly={subscribedOnly}
            />
          </TabsContent>
        </Tabs>
      )}
    </AppLayout>
  );
}
